import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import _ from 'lodash';

export function useStickyColumn(
  leftRef: React.RefObject<HTMLElement>,
  rightRef: React.RefObject<HTMLElement>,
  scrollContainerRef: React.RefObject<HTMLElement>,
  offset = 0,
) {
  const [shorterColumn, setShorterColumn] = useState<'left' | 'right' | null>(null);
  const [styleOverride, setStyleOverride] = useState<React.CSSProperties>();
  const [hasMeasuredOnce, setHasMeasuredOnce] = useState(false);
  const [isElSmallerThanContainer, setIsElSmallerThanContainer] = useState(false);
  
  const rafRef = useRef<number>();
  const wasStickyRef = useRef(false);

  const stableMeasure = useCallback(
    _.debounce(() => {
      if (!leftRef.current || !rightRef.current || !scrollContainerRef.current) return;

      const leftHeight = leftRef.current.offsetHeight;
      const rightHeight = rightRef.current.offsetHeight;
      const containerHeight = scrollContainerRef.current.offsetHeight;

      if (leftHeight < rightHeight) {
        setShorterColumn('left');
        setIsElSmallerThanContainer(leftHeight <= containerHeight);
      } else if (rightHeight < leftHeight) {
        setShorterColumn('right');
        setIsElSmallerThanContainer(rightHeight <= containerHeight);
      } else {
        setShorterColumn(null);
        setIsElSmallerThanContainer(false);
      }
    }, 100),
    [offset]
  );

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      stableMeasure();
    });
  }, [stableMeasure]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rafRef.current!);
      rafRef.current = requestAnimationFrame(() => {
        stableMeasure();
      });
    });

    if (leftRef.current) resizeObserver.observe(leftRef.current);
    if (rightRef.current) resizeObserver.observe(rightRef.current);
    if (scrollContainerRef.current) resizeObserver.observe(scrollContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [stableMeasure]);

  const updateSticky = useCallback(
    _.throttle((target: HTMLElement, scrollContainer: HTMLElement) => {
      if (!target || !scrollContainer) return;

      if (isElSmallerThanContainer) {
        setStyleOverride({
          position: 'sticky',
          top: `0px`,
        });
        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = target.getBoundingClientRect();
      const containerScrollTop = scrollContainer.scrollTop;

      const containerBottom = containerRect.bottom;
      const elementBottom = elementRect.bottom;
      const elementTop = elementRect.top;
      const elementHeight = elementRect.height;

      const hasScrolled = containerScrollTop > 0;
      const shouldStick = hasScrolled && (elementBottom > containerBottom || elementHeight <= containerRect.height);

      if (shouldStick && !wasStickyRef.current) {
        const stickyTop = elementHeight <= containerRect.height ? 0 : containerBottom - elementHeight;

        setStyleOverride({
          position: 'sticky',
          top: `${stickyTop - offset}px`,
        });
        wasStickyRef.current = true;
        return;
      }

      const buffer = 8;
      const fullyVisible =
        elementTop >= containerRect.top &&
        elementBottom <= containerBottom + buffer;

      if (wasStickyRef.current && fullyVisible) {
        setStyleOverride(undefined);
        wasStickyRef.current = false;
      }
    }, 100),
    [isElSmallerThanContainer, offset]
  );

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const target = shorterColumn === 'left' ? leftRef.current : rightRef.current;
    if (!scrollContainer || !target) return;

    const handleScroll = () => {
      updateSticky(target, scrollContainer);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    requestAnimationFrame(() => updateSticky(target, scrollContainer));

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [shorterColumn, updateSticky, scrollContainerRef]);

  useEffect(() => {
    if (shorterColumn !== null) setHasMeasuredOnce(true);
  }, [shorterColumn]);

  return {
    shorterColumn,
    styleOverride: hasMeasuredOnce ? styleOverride : undefined
  };
}
